import {Trans} from '@lingui/macro';
import {ellipsis} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {TextBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {CreateFormInputDisplay} from '../../../../../../components/common/createFormInputs/createFormInputDisplay';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../../../components/common/skeleton';
import {useClipboard} from '../../../../../../helpers/hooks/clipboardHookHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {WebhooksIntegrationSettingsModel} from '../../../../../../models/api/integrationModel';
import {fetchApiSecretAsyncAction} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../../banners/settingsBannersContext';

/*
 * Constants.
 */

const seeHowItWorksLink = 'https://dev.frontapp.com/reference/webhooks';
const secretPlaceholder = '******************************************';

/*
 * Props.
 */

interface SettingsAppsConfigureWebhooksProps {
  configurationSettings?: WebhooksIntegrationSettingsModel;
  setConfigurationSettings: HandlerOf<WebhooksIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledToggleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledToggleTextWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  gap: 4px;
`;

const StyledToggleTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
`;

const StyledToggleDescriptionDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledToggleDiv = styled.div`
  flex: 0 0 30px;
`;

const StyledApiSecretCreateFormInputDisplay = styled(CreateFormInputDisplay)`
  flex: 0 0 100%;
`;

const StyledApiSecretWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
  flex-wrap: wrap;
`;

const StyledApiSecretTextBox = styled(TextBox)`
  flex: 0 0 100%;
  ${ellipsis()};
`;

const StyledButton = styled(Button)`
  height: 30px;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const SettingsAppsConfigureWebhooks: FC<SettingsAppsConfigureWebhooksProps> = (props) => {
  const {configurationSettings, setConfigurationSettings} = props;
  const {navigateToLink} = useLinkNavigation();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const dispatch = useAppDispatch();
  const {showBanner} = useSettingsBanners();
  const {copy} = useClipboard();

  const [isFetchingSecret, setIsFetchingSecret] = useState(false);
  const [apiSecret, setApiSecret] = useState<string>();
  const [shouldRevealSecret, setShouldRevealSecret] = useState(false);

  const onToggleFullData = () => {
    if (!isGlobalAdmin) {
      return;
    }
    setConfigurationSettings({
      ...configurationSettings,
      sendFullEvent: !configurationSettings?.sendFullEvent,
    });
  };

  const fetchSecretAsync = async () => {
    try {
      setIsFetchingSecret(true);
      const secret = await dispatch(fetchApiSecretAsyncAction());
      setApiSecret(secret);
      return secret;
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="settings-apps-configure-app-dialog-webhooks-secret-failed-to-fetch">
            Failed to fetch client secret.
          </Trans>
        ),
      });
      return undefined;
    } finally {
      setIsFetchingSecret(false);
    }
  };

  const getAppSecretAsync = async () => {
    if (apiSecret) {
      return apiSecret;
    }
    const secret = await fetchSecretAsync();
    return secret;
  };

  const onCopySecretClickAsync = async () => {
    const secret = await getAppSecretAsync();
    if (!secret) {
      return;
    }

    copy(secret);
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: (
        <Trans id="settings-apps-configure-app-dialog-webhooks-secret-copied">
          Copied API secret to clipboard
        </Trans>
      ),
    });
  };

  const onToggleReveal = () => {
    setShouldRevealSecret((v) => !v);
    getAppSecretAsync();
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-webhooks-description">
        This integration adds new actions to the rules.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-webhooks-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {renderToggle(configurationSettings, isGlobalAdmin, onToggleFullData)}
      <StyledApiSecretCreateFormInputDisplay
        title={<Trans id="settings-apps-configure-app-dialog-webhooks-secret-title">API secret</Trans>}
        isDisabled
        input={
          <StyledApiSecretWrapperDiv>
            {!isFetchingSecret && (
              <StyledApiSecretTextBox
                value={shouldRevealSecret ? apiSecret : secretPlaceholder}
                isDisabled
                noMargin
              />
            )}
            {isFetchingSecret && <Skeleton height="30px" />}
            <ButtonGroupSpacer />
            <StyledButton onClick={onCopySecretClickAsync} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name="copySmall" />
                <Trans id="settings-apps-configure-app-dialog-webhooks-secret-copy">Copy</Trans>
              </StyledButtonContentWrapperDiv>
            </StyledButton>
            <StyledButton onClick={onToggleReveal} isDisabled={isFetchingSecret}>
              <StyledButtonContentWrapperDiv>
                <StyledIcon name={shouldRevealSecret ? 'eyeCrossedSmall' : 'eyeSmall'} />
                {!shouldRevealSecret && (
                  <Trans id="settings-apps-configure-app-dialog-webhooks-secret-reveal">Reveal</Trans>
                )}
                {shouldRevealSecret && (
                  <Trans id="settings-apps-configure-app-dialog-webhooks-secret-hide">Hide</Trans>
                )}
              </StyledButtonContentWrapperDiv>
            </StyledButton>
          </StyledApiSecretWrapperDiv>
        }
      />
    </>
  );
};

/*
 * Helpers.
 */

function renderToggle(
  configurationSettings: WebhooksIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onToggleFullData: Handler,
) {
  return (
    <StyledToggleWrapperDiv>
      <StyledToggleTextWrapperDiv onClick={onToggleFullData}>
        <StyledToggleTitleDiv>
          <Trans id="settings-apps-configure-app-dialog-webhooks-toggle-title">Send Full Event Data</Trans>
        </StyledToggleTitleDiv>
        <StyledToggleDescriptionDiv>
          <Trans id="settings-apps-configure-app-dialog-webhooks-toggle-description">
            Turn this option on to send full event data to webhooks.
          </Trans>
        </StyledToggleDescriptionDiv>
      </StyledToggleTextWrapperDiv>
      <StyledToggleDiv>
        <Toggle
          isChecked={Boolean(configurationSettings?.sendFullEvent)}
          onChange={onToggleFullData}
          size={VisualSizesEnum.SMALL}
          isDisabled={!isGlobalAdmin}
        />
      </StyledToggleDiv>
    </StyledToggleWrapperDiv>
  );
}
